import { supabase } from '@/integrations/supabase/client';
import { PostgrestError } from '@supabase/supabase-js';
import type {
  CustomReport,
  CustomReportConfig,
  CustomReportFolder,
  CustomReportFormData,
  ReportConfig,
} from '@/types/custom-reports';

// ============================================================================
// TYPES FOR SERVICE RESPONSES
// ============================================================================

export interface ServiceResult<T> {
  data: T | null;
  error: PostgrestError | string | null;
}

export interface CustomReportWithConfig extends CustomReport {
  config: ReportConfig;
}

// ============================================================================
// CUSTOM REPORTS SERVICE
// ============================================================================

class CustomReportsService {
  /**
   * Get all custom reports for the current user's company
   */
  async getReports(options?: {
    favoritesOnly?: boolean;
    search?: string;
  }): Promise<ServiceResult<CustomReportWithConfig[]>> {
    try {
      let query = supabase
        .from('custom_reports')
        .select(`
          *,
          custom_report_configs(config)
        `)
        .order('created_at', { ascending: false });

      // Filter by favorites if requested
      if (options?.favoritesOnly) {
        query = query.eq('is_favorite', true);
      }

      // Search by name if provided (sanitize input to prevent SQL injection patterns)
      if (options?.search) {
        // Escape special SQL characters: % _ \
        const sanitizedSearch = options.search.replace(/[%_\\]/g, '\\$&');
        query = query.ilike('name', `%${sanitizedSearch}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to include config
      const reports: CustomReportWithConfig[] = (data || []).map((report: any) => ({
        ...report,
        config: report.custom_report_configs?.[0]?.config || null,
      }));

      return { data: reports, error: null };
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Get a single report by ID with its config
   */
  async getReportById(id: string): Promise<ServiceResult<CustomReportWithConfig>> {
    try {
      const { data, error } = await supabase
        .from('custom_reports')
        .select(`
          *,
          custom_report_configs(config)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      const report: CustomReportWithConfig = {
        ...data,
        config: data.custom_report_configs?.[0]?.config || null,
      };

      return { data: report, error: null };
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Create a new custom report with its config
   */
  async createReport(formData: CustomReportFormData): Promise<ServiceResult<CustomReportWithConfig>> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      // Get user's company from profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .limit(1)
        .single();

      if (!profileData?.company_id) {
        return { data: null, error: 'Company not found' };
      }

      // Start a transaction by creating the report first
      const { data: report, error: reportError } = await supabase
        .from('custom_reports')
        .insert({
          company_id: profileData.company_id,
          created_by: user.id,
          name: formData.name,
          description: formData.description || null,
          is_favorite: formData.is_favorite || false,
          is_public: formData.is_public || false,
          folder_id: formData.folder_id || null,
        })
        .select()
        .single();

      if (reportError) throw reportError;
      if (!report) throw new Error('Failed to create report');

      // Create the config
      const { error: configError } = await supabase
        .from('custom_report_configs')
        .insert({
          report_id: report.id,
          config: formData.config,
        });

      if (configError) throw configError;

      // Fetch the complete report with config
      return await this.getReportById(report.id);
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Update an existing report
   */
  async updateReport(
    id: string,
    formData: Partial<CustomReportFormData>
  ): Promise<ServiceResult<CustomReportWithConfig>> {
    try {
      // Update report metadata if provided
      if (formData.name || formData.description !== undefined ||
          formData.is_favorite !== undefined || formData.is_public !== undefined ||
          formData.folder_id !== undefined) {
        const { error: updateError } = await supabase
          .from('custom_reports')
          .update({
            name: formData.name,
            description: formData.description || null,
            is_favorite: formData.is_favorite ?? undefined,
            is_public: formData.is_public ?? undefined,
            folder_id: formData.folder_id ?? undefined,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);

        if (updateError) throw updateError;
      }

      // Update config if provided
      if (formData.config) {
        const { error: configError } = await supabase
          .from('custom_report_configs')
          .update({ config: formData.config })
          .eq('report_id', id);

        if (configError) throw configError;
      }

      // Fetch and return the updated report
      return await this.getReportById(id);
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Delete a report
   */
  async deleteReport(id: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('custom_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Duplicate a report (creates a copy with "(Copy)" suffix)
   */
  async duplicateReport(id: string): Promise<ServiceResult<CustomReportWithConfig>> {
    try {
      // Get the original report
      const { data: original, error: fetchError } = await this.getReportById(id);

      if (fetchError || !original) {
        return { data: null, error: fetchError || 'Report not found' };
      }

      // Create a copy
      return await this.createReport({
        name: `${original.name} (cópia)`,
        description: original.description || '',
        is_favorite: false,
        is_public: original.is_public,
        folder_id: original.folder_id || undefined,
        config: original.config,
      });
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Toggle favorite status
   */
  async toggleFavorite(id: string): Promise<ServiceResult<CustomReportWithConfig>> {
    try {
      // First get current status
      const { data: current } = await supabase
        .from('custom_reports')
        .select('is_favorite')
        .eq('id', id)
        .single();

      if (!current) throw new Error('Report not found');

      // Toggle the status
      const { error } = await supabase
        .from('custom_reports')
        .update({ is_favorite: !current.is_favorite })
        .eq('id', id);

      if (error) throw error;

      return await this.getReportById(id);
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  // ============================================================================
  // FOLDERS
  // ============================================================================

  /**
   * Get all folders for the current user's company
   */
  async getFolders(): Promise<ServiceResult<CustomReportFolder[]>> {
    try {
      const { data, error } = await supabase
        .from('custom_report_folders')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Create a new folder
   */
  async createFolder(name: string, parentId?: string): Promise<ServiceResult<CustomReportFolder>> {
    try {
      // Get current user's company from profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .limit(1)
        .single();

      if (!profileData?.company_id) {
        return { data: null, error: 'Company not found' };
      }

      const { data, error } = await supabase
        .from('custom_report_folders')
        .insert({
          company_id: profileData.company_id,
          name,
          parent_id: parentId || null,
        })
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Delete a folder (validates ownership first)
   */
  async deleteFolder(id: string): Promise<ServiceResult<void>> {
    try {
      // First verify ownership - folder must belong to user's company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      const { data: folder } = await supabase
        .from('custom_report_folders')
        .select('id, company_id')
        .eq('id', id)
        .single();

      if (!folder) {
        return { data: null, error: 'Folder not found or access denied' };
      }

      // Verify folder belongs to user's company (via profile)
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .eq('company_id', folder.company_id)
        .limit(1)
        .single();

      if (!profileData) {
        return { data: null, error: 'Access denied' };
      }

      const { error } = await supabase
        .from('custom_report_folders')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as PostgrestError };
    }
  }
}

// Export singleton instance
export const customReportsService = new CustomReportsService();
